import React from "react";
import { ArrowLeft } from "react-feather";
import { merge } from "lodash-es";

import { createPropsProvider } from "@kikoff/client-utils/src/react";
import ContainerButton from "@kikoff/components/src/v1/buttons/ContainerButton";
import { PageControllerV1 } from "@kikoff/hooks/src/usePageController";
import { cls, combineClasses } from "@kikoff/utils/src/string";

import usePageFlowContext from "@component/flows/hooks/usePageFlowContext";

import PageAlign, { PageAlignProps } from "../offer/page_align";

import styles from "./page_template.module.scss";

interface PageTemplateProps {
  className?: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  fixedHeader?: boolean;
  contentFor?: { aboveTitle?: React.ReactNode };
  inlineTitle?: boolean;
  propsFor?: {
    title?: React.ComponentProps<"header">;
    subtitle?: React.ComponentProps<"div">;
    content?: React.ComponentProps<"div">;
    pageAlign?: PageAlignProps;
  };
  noBack?: boolean;
  onBack?(context: {
    pageController: PageControllerV1;
    defaultPrevented: boolean;
    preventDefault(): void;
  }): void;
  subtitle?: React.ReactNode;
}

export default function PageTemplate(props: PageTemplateProps) {
  const {
    className,
    title,
    children,
    action,
    fixedHeader,
    inlineTitle = false,
    contentFor = {},
    propsFor = {},
    noBack = false,
    onBack,
    subtitle,
  } = PageTemplate.PropsProvider.useMerge(props);
  const { pageController } = usePageFlowContext();

  const titleElementWith = ({ className = "" }) => (
    <header
      {...propsFor.title}
      data-cy="title"
      className={cls("color:strong", className, propsFor.title?.className)}
    >
      {title}
    </header>
  );

  const top = (
    <div className={styles.top}>
      {!noBack && (
        <ContainerButton
          fit
          onClick={() => {
            if (onBack)
              onBack({
                pageController,
                defaultPrevented: false,
                preventDefault() {
                  // eslint-disable-next-line react/no-this-in-sfc
                  this.defaultPrevented = true;
                },
              });
            else pageController.history.back();
          }}
          data-cy="page-template-back"
        >
          <ArrowLeft strokeWidth={3} />
        </ContainerButton>
      )}
      {inlineTitle && titleElementWith({ className: "text:regular+" })}
      <div className={styles.action}>{action}</div>
    </div>
  );

  return (
    <>
      {fixedHeader && (
        <PageAlign className={styles.fixed} wrap>
          {top}
        </PageAlign>
      )}
      <PageAlign scroll {...propsFor.pageAlign}>
        <div
          className={combineClasses(
            styles["page-template"],
            fixedHeader && styles["fixed-header"],
            className
          )}
        >
          {top}
          {contentFor.aboveTitle}
          {!inlineTitle &&
            titleElementWith({ className: "text:heading-3 mb-1" })}
          <div
            {...propsFor.subtitle}
            className={cls("mb-2", propsFor.subtitle?.className)}
          >
            {subtitle}
          </div>
          <div {...propsFor.content}>{children}</div>
        </div>
      </PageAlign>
    </>
  );
}

PageTemplate.PropsProvider = createPropsProvider<PageTemplateProps>(
  "PageTemplate",
  {
    onBack: (a, b) =>
      (a.onBack || b.onBack) &&
      ((context) => {
        b.onBack?.(context);
        if (context.defaultPrevented) return;
        a.onBack?.(context);
      }),
    propsFor: (a, b) => merge(a.propsFor, b.propsFor),
  }
);
